import { TextField, TextAreaField, ListEditor, Field, VisibilityToggle } from "./EditorFields";
import ImageUploader from "./ImageUploader";

const ICON_HINT = "Icon names: GraduationCap, FlaskConical, Users, Sparkles, ShieldCheck, Award, BookOpenCheck, Pill, FlaskRound, Microscope, Stethoscope, Beaker";

export function SettingsEditor({ data, onChange }) {
  const set = (k) => (v) => onChange({ ...data, [k]: v });
  return (
    <div className="grid sm:grid-cols-2 gap-5">
      <TextField label="Institute Name" value={data.institute_name} onChange={set("institute_name")} testid="settings-institute_name" />
      <TextField label="Short Name" value={data.short_name} onChange={set("short_name")} hint="Big text next to the logo, e.g. Sunbright" testid="settings-short_name" />
      <TextField label="Tagline (under short name)" value={data.tagline} onChange={set("tagline")} placeholder="Pharma Academy" hint="Small caps text under the short name in header & footer. Leave blank to hide." testid="settings-tagline" />
      <div className="sm:col-span-2">
        <ImageUploader label="Logo Image" value={data.logo_url} onChange={set("logo_url")} hint="Used in the header, footer and browser favicon." testid="settings-logo" />
      </div>
      <TextField label="Email" value={data.email} onChange={set("email")} testid="settings-email" />
      <TextField label="Phone (with +)" value={data.phone} onChange={set("phone")} hint="e.g. +918108611543" testid="settings-phone" />
      <TextField label="Phone (display)" value={data.phone_display} onChange={set("phone_display")} testid="settings-phone_display" />
      <TextField label="WhatsApp Number" value={data.whatsapp} onChange={set("whatsapp")} hint="No '+', e.g. 918108611543" testid="settings-whatsapp" />
      <TextField label="Address" value={data.address} onChange={set("address")} testid="settings-address" />
      <TextField label="Hours" value={data.hours} onChange={set("hours")} testid="settings-hours" />
      <TextField label="Map Query" value={data.map_query} onChange={set("map_query")} hint="Used by Google Maps embed" testid="settings-map_query" />
      <div className="sm:col-span-2">
        <TextAreaField label="WhatsApp Pre-filled Message" value={data.whatsapp_message} onChange={set("whatsapp_message")} testid="settings-whatsapp_message" />
      </div>
      <div className="sm:col-span-2 rounded-xl border border-border bg-white p-5 space-y-4">
        <h3 className="font-display text-lg font-medium">Browser tab & SEO</h3>
        <TextField label="Tab Title" value={data.tab_title} onChange={set("tab_title")} placeholder="Sunbright Scholars Academy" hint="Shown in the browser tab and in Google search results." testid="settings-tab_title" />
        <TextAreaField label="Meta Description" value={data.meta_description} onChange={set("meta_description")} rows={3} hint="Short description used by Google & social previews (~150 chars)." testid="settings-meta_description" />
      </div>
    </div>
  );
}

export function HeroEditor({ data, onChange }) {
  const set = (k) => (v) => onChange({ ...data, [k]: v });
  const stats = data.stats || [];
  return (
    <div className="space-y-5">
      <VisibilityToggle enabled={data.enabled} onChange={set("enabled")} testid="hero-visibility" label="Show Hero section" />
      <TextField label="Badge" value={data.badge} onChange={set("badge")} testid="hero-badge" />
      <div className="grid sm:grid-cols-2 gap-5">
        <TextField label="Headline (top line)" value={data.headline_top} onChange={set("headline_top")} testid="hero-headline_top" />
        <TextField label="Headline (bottom line)" value={data.headline_bottom} onChange={set("headline_bottom")} testid="hero-headline_bottom" />
      </div>
      <TextAreaField label="Sub-headline" value={data.subheadline} onChange={set("subheadline")} rows={3} testid="hero-subheadline" />
      <ImageUploader label="Hero Image" value={data.image_url} onChange={set("image_url")} testid="hero-image" />
      <div className="grid sm:grid-cols-2 gap-5">
        <TextField label="Image Badge Title" value={data.image_badge_title} onChange={set("image_badge_title")} testid="hero-image_badge_title" />
        <TextField label="Image Badge Subtitle" value={data.image_badge_subtitle} onChange={set("image_badge_subtitle")} testid="hero-image_badge_subtitle" />
        <TextField label="Floating Badge Title" value={data.floating_badge_title} onChange={set("floating_badge_title")} testid="hero-floating_badge_title" />
        <TextField label="Floating Badge Value" value={data.floating_badge_value} onChange={set("floating_badge_value")} testid="hero-floating_badge_value" />
      </div>

      <div className="rounded-xl border border-border bg-white p-5 space-y-4">
        <h3 className="font-display text-lg font-medium">Right column — Lead form</h3>
        <p className="text-xs text-muted-foreground">The right side of the hero shows a quick enquiry form. Customise its copy here.</p>
        <TextField label="Form Heading" value={data.form_heading} onChange={set("form_heading")} placeholder="Reserve your seat today." testid="hero-form_heading" />
        <TextAreaField label="Form Body" value={data.form_body} onChange={set("form_body")} rows={2} placeholder="Fill in your details — a counsellor calls you back within the hour." testid="hero-form_body" />
        <TextField label="Submit Button Label" value={data.form_submit_label} onChange={set("form_submit_label")} placeholder="Get a call back" testid="hero-form_submit_label" />
      </div>
      <ListEditor
        title="Hero Stat Badges (max 3)"
        items={stats}
        onChange={(items) => onChange({ ...data, stats: items.slice(0, 3) })}
        makeBlank={() => ({ value: "", label: "" })}
        testid="hero-stats"
        renderItem={(item, update) => (
          <div className="grid sm:grid-cols-2 gap-4">
            <TextField label="Value" value={item.value} onChange={(v) => update({ ...item, value: v })} />
            <TextField label="Label" value={item.label} onChange={(v) => update({ ...item, label: v })} />
          </div>
        )}
      />
    </div>
  );
}

export function AboutEditor({ data, onChange }) {
  const set = (k) => (v) => onChange({ ...data, [k]: v });
  return (
    <div className="space-y-5">
      <VisibilityToggle enabled={data.enabled} onChange={set("enabled")} testid="about-visibility" label="Show About section (and 'About' nav link)" />
      <TextField label="Eyebrow" value={data.eyebrow} onChange={set("eyebrow")} testid="about-eyebrow" />
      <TextField label="Heading" value={data.heading} onChange={set("heading")} testid="about-heading" />
      <TextAreaField label="Body" value={data.body} onChange={set("body")} rows={4} testid="about-body" />
      <ImageUploader label="About Image" value={data.image_url} onChange={set("image_url")} testid="about-image" />
      <ListEditor
        title="Highlight Points"
        items={data.points || []}
        onChange={(items) => onChange({ ...data, points: items })}
        makeBlank={() => ({ icon: "Sparkles", title: "", desc: "" })}
        testid="about-points"
        renderItem={(item, update) => (
          <div className="grid sm:grid-cols-2 gap-4">
            <TextField label="Icon" value={item.icon} onChange={(v) => update({ ...item, icon: v })} hint={ICON_HINT} />
            <TextField label="Title" value={item.title} onChange={(v) => update({ ...item, title: v })} />
            <div className="sm:col-span-2">
              <TextAreaField label="Description" value={item.desc} onChange={(v) => update({ ...item, desc: v })} rows={2} />
            </div>
          </div>
        )}
      />
    </div>
  );
}

export function CoursesEditor({ data, onChange }) {
  const set = (k) => (v) => onChange({ ...data, [k]: v });
  return (
    <div className="space-y-5">
      <VisibilityToggle enabled={data.enabled} onChange={set("enabled")} testid="courses-visibility" label="Show Courses section (and 'Courses' nav link)" />
      <TextField label="Eyebrow" value={data.eyebrow} onChange={set("eyebrow")} testid="courses-eyebrow" />
      <TextField label="Heading" value={data.heading} onChange={set("heading")} testid="courses-heading" />
      <TextAreaField label="Subheading" value={data.subheading} onChange={set("subheading")} testid="courses-subheading" />
      <TextField label="Default Card Button Label" value={data.cta_label} onChange={set("cta_label")} placeholder="Enroll Now" hint="Fallback used when a course has no custom button label." testid="courses-cta_label" />
      <ListEditor
        title="Courses"
        items={data.items || []}
        onChange={(items) => onChange({ ...data, items })}
        makeBlank={() => ({ id: `course-${Date.now()}`, title: "", short: "", duration: "", icon: "Pill", desc: "", featured: false, bullets: ["", "", ""] })}
        testid="courses-items"
        renderItem={(item, update) => (
          <div className="grid sm:grid-cols-2 gap-4">
            <TextField label="ID (unique)" value={item.id} onChange={(v) => update({ ...item, id: v })} hint="Lowercase, no spaces" />
            <TextField label="Short Code" value={item.short} onChange={(v) => update({ ...item, short: v })} hint="e.g. GPAT" />
            <TextField label="Title" value={item.title} onChange={(v) => update({ ...item, title: v })} />
            <TextField label="Duration" value={item.duration} onChange={(v) => update({ ...item, duration: v })} />
            <TextField label="Button Label (this card)" value={item.cta_label} onChange={(v) => update({ ...item, cta_label: v })} placeholder="Enroll Now" hint="Leave blank to use the default above." />
            <TextField label="Icon" value={item.icon} onChange={(v) => update({ ...item, icon: v })} hint={ICON_HINT} />
            <label className="flex items-center gap-2 self-end">
              <input type="checkbox" checked={!!item.featured} onChange={(e) => update({ ...item, featured: e.target.checked })} />
              <span className="text-sm">Show &quot;Most Joined&quot; badge</span>
            </label>
            <div className="sm:col-span-2">
              <TextAreaField label="Description" value={item.desc} onChange={(v) => update({ ...item, desc: v })} rows={2} />
            </div>
            <div className="sm:col-span-2">
              <span className="text-xs uppercase tracking-widest text-muted-foreground">Bullet Points (3 recommended)</span>
              <div className="mt-1.5 space-y-2">
                {[0, 1, 2].map((i) => (
                  <TextField
                    key={i}
                    label={`Bullet ${i + 1}`}
                    value={(item.bullets || [])[i] || ""}
                    onChange={(v) => {
                      const next = [...(item.bullets || ["", "", ""])];
                      while (next.length < 3) next.push("");
                      next[i] = v;
                      update({ ...item, bullets: next });
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
      />
    </div>
  );
}

export function FacultyEditor({ data, onChange }) {
  const set = (k) => (v) => onChange({ ...data, [k]: v });
  return (
    <div className="space-y-5">
      <VisibilityToggle enabled={data.enabled} onChange={set("enabled")} testid="faculty-visibility" label="Show Faculty section (and 'Faculty' nav link)" />
      <TextField label="Eyebrow" value={data.eyebrow} onChange={set("eyebrow")} testid="faculty-eyebrow" />
      <TextField label="Heading" value={data.heading} onChange={set("heading")} testid="faculty-heading" />
      <TextAreaField label="Body" value={data.body} onChange={set("body")} rows={3} testid="faculty-body" />
      <ImageUploader label="Side Image (next to faculty cards)" value={data.image_url} onChange={set("image_url")} testid="faculty-image" />
      <ListEditor
        title="Faculty Members"
        items={data.members || []}
        onChange={(items) => onChange({ ...data, members: items })}
        makeBlank={() => ({ name: "", role: "", phone: "" })}
        testid="faculty-members"
        renderItem={(item, update) => (
          <div className="grid sm:grid-cols-2 gap-4">
            <TextField label="Name" value={item.name} onChange={(v) => update({ ...item, name: v })} />
            <TextField label="Role" value={item.role} onChange={(v) => update({ ...item, role: v })} />
            <div className="sm:col-span-2">
              <TextField
                label="Phone Number"
                value={item.phone}
                onChange={(v) => update({ ...item, phone: v })}
                placeholder="+91 98XXX XXXXX"
                hint="Shown as a 'Call' button on the card. Tapping it dials this number."
              />
            </div>
          </div>
        )}
      />
    </div>
  );
}

export function AchievementsEditor({ data, onChange }) {
  const set = (k) => (v) => onChange({ ...data, [k]: v });
  return (
    <div className="space-y-5">
      <VisibilityToggle enabled={data.enabled} onChange={set("enabled")} testid="achievements-visibility" label="Show Achievements section (and 'Results' nav link)" />
      <TextField label="Eyebrow" value={data.eyebrow} onChange={set("eyebrow")} testid="achievements-eyebrow" />
      <TextField label="Heading" value={data.heading} onChange={set("heading")} testid="achievements-heading" />
      <ListEditor
        title="Counters"
        items={data.stats || []}
        onChange={(items) => onChange({ ...data, stats: items })}
        makeBlank={() => ({ value: 0, suffix: "+", label: "" })}
        testid="achievements-stats"
        renderItem={(item, update) => (
          <div className="grid sm:grid-cols-3 gap-4">
            <TextField label="Value (number)" type="number" value={item.value} onChange={(v) => update({ ...item, value: Number(v) || 0 })} />
            <TextField label="Suffix" value={item.suffix} onChange={(v) => update({ ...item, suffix: v })} hint="e.g. +, %" />
            <TextField label="Label" value={item.label} onChange={(v) => update({ ...item, label: v })} />
          </div>
        )}
      />
    </div>
  );
}

export function TestimonialsEditor({ data, onChange }) {
  const set = (k) => (v) => onChange({ ...data, [k]: v });
  return (
    <div className="space-y-5">
      <VisibilityToggle enabled={data.enabled} onChange={set("enabled")} testid="testimonials-visibility" label="Show Testimonials section (and 'Testimonials' nav link)" />
      <TextField label="Eyebrow" value={data.eyebrow} onChange={set("eyebrow")} testid="testimonials-eyebrow" />
      <TextField label="Heading" value={data.heading} onChange={set("heading")} testid="testimonials-heading" />
      <ListEditor
        title="Testimonials"
        items={data.items || []}
        onChange={(items) => onChange({ ...data, items })}
        makeBlank={() => ({ name: "", course: "", text: "" })}
        testid="testimonials-items"
        renderItem={(item, update) => (
          <div className="grid sm:grid-cols-2 gap-4">
            <TextField label="Student Name" value={item.name} onChange={(v) => update({ ...item, name: v })} />
            <TextField label="Course / Achievement" value={item.course} onChange={(v) => update({ ...item, course: v })} />
            <div className="sm:col-span-2">
              <TextAreaField label="Testimonial" value={item.text} onChange={(v) => update({ ...item, text: v })} rows={3} />
            </div>
          </div>
        )}
      />
    </div>
  );
}

export function TickerEditor({ data, onChange }) {
  const set = (k) => (v) => onChange({ ...data, [k]: v });
  return (
    <div className="space-y-5">
      <VisibilityToggle enabled={data.enabled} onChange={set("enabled")} testid="ticker-visibility" label="Show ticker under the hero" />
      <div className="grid sm:grid-cols-3 gap-5">
        <TextField label="Background Color" value={data.background_color} onChange={set("background_color")} hint="HEX, e.g. #047857" testid="ticker-bg" />
        <TextField label="Text Color" value={data.text_color} onChange={set("text_color")} hint="HEX, e.g. #ffffff" testid="ticker-text" />
        <TextField label="Speed (seconds per loop)" type="number" value={data.speed_seconds} onChange={(v) => set("speed_seconds")(Number(v) || 38)} hint="Lower = faster. 28-45 feels natural." testid="ticker-speed" />
      </div>
      <ListEditor
        title="Ticker Items"
        items={data.items || []}
        onChange={(items) => onChange({ ...data, items })}
        makeBlank={() => ""}
        testid="ticker-items"
        renderItem={(item, update) => (
          <TextField label="Text" value={item} onChange={(v) => update(v)} />
        )}
      />
    </div>
  );
}

export function OffersEditor({ data, onChange }) {
  const set = (k) => (v) => onChange({ ...data, [k]: v });
  return (
    <div className="space-y-5">
      <VisibilityToggle enabled={data.enabled} onChange={set("enabled")} testid="offers-visibility" label="Show Offers section below Courses" />
      <TextField label="Eyebrow" value={data.eyebrow} onChange={set("eyebrow")} testid="offers-eyebrow" />
      <TextField label="Heading" value={data.heading} onChange={set("heading")} testid="offers-heading" />
      <TextAreaField label="Subheading" value={data.subheading} onChange={set("subheading")} rows={2} testid="offers-subheading" />
      <ListEditor
        title="Offer Cards (3 recommended — center card is highlighted)"
        items={data.items || []}
        onChange={(items) => onChange({ ...data, items })}
        makeBlank={() => ({
          id: `offer-${Date.now()}`,
          background_color: "#1e2c6b", text_color: "#ffffff",
          icon: "Sparkles", title: "", value: "", body: "", pill: "", ribbon: "",
          button_label: "Inquire Now", button_action: "enroll",
          button_bg_color: "#ffffff", button_text_color: "#1e2c6b",
        })}
        testid="offers-items"
        renderItem={(item, update) => (
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <TextField label="ID" value={item.id} onChange={(v) => update({ ...item, id: v })} />
              <TextField label="Icon" value={item.icon} onChange={(v) => update({ ...item, icon: v })} hint="Users, Percent, CalendarCheck, Gift, Tag, Star, Trophy, Flame, Clock" />
              <TextField label="Title" value={item.title} onChange={(v) => update({ ...item, title: v })} />
              <TextField label="Big Value (optional)" value={item.value} onChange={(v) => update({ ...item, value: v })} hint="e.g. 20% — shown extra large above the title" />
              <div className="sm:col-span-2">
                <TextAreaField label="Body" value={item.body} onChange={(v) => update({ ...item, body: v })} rows={2} />
              </div>
              <TextField label="Pill Text (optional)" value={item.pill} onChange={(v) => update({ ...item, pill: v })} hint="e.g. Only 25 Seats Per Batch" />
              <TextField label="Ribbon Text (optional)" value={item.ribbon} onChange={(v) => update({ ...item, ribbon: v })} hint="Diagonal corner ribbon, e.g. Limited Time" />
            </div>

            <div className="rounded-lg border border-border/60 p-4 bg-white space-y-3">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Card Colors</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <TextField label="Background Color" value={item.background_color} onChange={(v) => update({ ...item, background_color: v })} hint="HEX, e.g. #1e2c6b" />
                <TextField label="Text Color" value={item.text_color} onChange={(v) => update({ ...item, text_color: v })} hint="HEX, e.g. #ffffff" />
              </div>
            </div>

            <div className="rounded-lg border border-border/60 p-4 bg-white space-y-3">
              <p className="text-xs uppercase tracking-widest text-muted-foreground">Button</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <TextField label="Button Label" value={item.button_label} onChange={(v) => update({ ...item, button_label: v })} />
                <Field label="Button Action">
                  <select
                    value={item.button_action || "enroll"}
                    onChange={(e) => update({ ...item, button_action: e.target.value })}
                    className="h-10 w-full rounded-md border border-input bg-white px-3 text-sm"
                  >
                    <option value="enroll">Open enrollment form</option>
                    <option value="call">Call (uses Settings phone)</option>
                    <option value="whatsapp">WhatsApp (uses Settings number)</option>
                  </select>
                </Field>
                <TextField label="Button Background" value={item.button_bg_color} onChange={(v) => update({ ...item, button_bg_color: v })} hint="HEX" />
                <TextField label="Button Text Color" value={item.button_text_color} onChange={(v) => update({ ...item, button_text_color: v })} hint="HEX" />
              </div>
            </div>
          </div>
        )}
      />
    </div>
  );
}

export function ContactEditor({ data, onChange }) {
  const set = (k) => (v) => onChange({ ...data, [k]: v });
  return (
    <div className="space-y-5">
      <VisibilityToggle enabled={data.enabled} onChange={set("enabled")} testid="contact-visibility" label="Show Contact section (and 'Contact' nav link)" />
      <TextField label="Eyebrow" value={data.eyebrow} onChange={set("eyebrow")} testid="contact-eyebrow" />
      <TextField label="Heading" value={data.heading} onChange={set("heading")} testid="contact-heading" />
      <TextAreaField label="Body" value={data.body} onChange={set("body")} rows={3} testid="contact-body" />

      <div className="rounded-xl border border-border bg-white p-5 space-y-4">
        <h3 className="font-display text-lg font-medium">Inline Lead Form (right column)</h3>
        <p className="text-xs text-muted-foreground">
          The right column of the contact section shows an inline enquiry form. Customise its copy here.
        </p>
        <TextField label="Form Eyebrow" value={data.form_eyebrow} onChange={set("form_eyebrow")} placeholder="Quick Enquiry" testid="contact-form_eyebrow" />
        <TextField label="Form Heading" value={data.form_heading} onChange={set("form_heading")} testid="contact-form_heading" />
        <TextAreaField label="Form Body" value={data.form_body} onChange={set("form_body")} rows={2} testid="contact-form_body" />
        <TextField label="Submit Button Label" value={data.form_submit_label} onChange={set("form_submit_label")} placeholder="Send enquiry" testid="contact-form_submit_label" />
      </div>
      <p className="text-xs text-muted-foreground">
        Phone, WhatsApp, email, address, hours and map query come from the <strong>Settings</strong> tab. The address row opens directly in Google Maps.
      </p>
    </div>
  );
}

export function FooterEditor({ data, onChange }) {
  const set = (k) => (v) => onChange({ ...data, [k]: v });
  return (
    <div className="space-y-5">
      <VisibilityToggle enabled={data.enabled} onChange={set("enabled")} testid="footer-visibility" label="Show site footer" />

      <TextAreaField label="Description (under logo)" value={data.description} onChange={set("description")} rows={3} testid="footer-description" />

      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={data.show_contact_block !== false}
          onChange={(e) => set("show_contact_block")(e.target.checked)}
          data-testid="footer-show_contact_block"
        />
        <span className="text-sm">Show address, phone & email under the description (from Settings)</span>
      </label>

      <ListEditor
        title="Link Groups"
        items={data.link_groups || []}
        onChange={(items) => onChange({ ...data, link_groups: items })}
        makeBlank={() => ({ title: "New Group", links: [{ label: "", href: "" }] })}
        testid="footer-link_groups"
        renderItem={(item, update) => (
          <div className="space-y-4">
            <TextField label="Group Title" value={item.title} onChange={(v) => update({ ...item, title: v })} />
            <ListEditor
              title="Links"
              items={item.links || []}
              onChange={(links) => update({ ...item, links })}
              makeBlank={() => ({ label: "", href: "#" })}
              testid={`footer-links-${item.title || 'group'}`}
              renderItem={(link, updateLink) => (
                <div className="grid sm:grid-cols-2 gap-4">
                  <TextField label="Label" value={link.label} onChange={(v) => updateLink({ ...link, label: v })} />
                  <TextField label="URL / Anchor" value={link.href} onChange={(v) => updateLink({ ...link, href: v })} hint="e.g. #about or https://..." />
                </div>
              )}
            />
          </div>
        )}
      />

      <div className="grid sm:grid-cols-2 gap-5">
        <TextField label="Side Column Eyebrow" value={data.side_eyebrow} onChange={set("side_eyebrow")} testid="footer-side_eyebrow" />
        <TextField label="Staff Login Link Text" value={data.staff_login_label} onChange={set("staff_login_label")} placeholder="Staff login" hint="Leave blank to hide. Always opens /admin/login." testid="footer-staff_login_label" />
        <div className="sm:col-span-2">
          <TextAreaField label="Side Column Body" value={data.side_body} onChange={set("side_body")} rows={3} testid="footer-side_body" />
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-5">
        <TextField label="Copyright Text" value={data.copyright_text} onChange={set("copyright_text")} placeholder="All rights reserved." hint="Shown after the year + institute name." testid="footer-copyright_text" />
        <TextField label="Tagline Line (right side)" value={data.tagline_line} onChange={set("tagline_line")} testid="footer-tagline_line" />
      </div>
    </div>
  );
}
